import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import type { Deposit, EstimateType, SalesSettings } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select } from "@clevis/shared-ui";
import { call, CallError } from "../lib/callFunction";
import { textareaClass } from "./JobEditCard";
import { useDepartments } from "../hooks/useDepartments";
import { TermsLoader } from "./TermsLoader";

// Admin: estimate types (Commercial, Service…) with their terms, deposit and
// how long the price holds; the invoice terms; and how commissions work.

export function DepositInput({ value, onChange, compact = false }: { value: Deposit | null; onChange: (d: Deposit | null) => void; compact?: boolean }) {
  const kind = value?.kind ?? "none";
  const shown = value ? (value.kind === "percent" ? String(value.value / 100) : (value.value / 100).toFixed(2)) : "";
  return (
    <div className="flex items-center gap-1.5">
      <Select
        className={compact ? "w-36 py-1 text-xs" : "w-40"}
        value={kind}
        aria-label="Deposit"
        onChange={(e) => {
          const k = e.target.value;
          onChange(k === "none" ? null : { kind: k as Deposit["kind"], value: k === "percent" ? 2500 : 50000 });
        }}
      >
        <option value="none">No deposit</option>
        <option value="percent">Percent of total</option>
        <option value="fixed">Fixed amount</option>
      </Select>
      {value ? (
        <div className="relative">
          {value.kind === "fixed" ? <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-steel">$</span> : null}
          <Input
            type="number"
            min="0"
            step={value.kind === "percent" ? "1" : "0.01"}
            className={`${compact ? "w-24 py-1 text-xs" : "w-28"} ${value.kind === "fixed" ? "pl-5" : "pr-6"}`}
            value={shown}
            aria-label={value.kind === "percent" ? "Deposit percent" : "Deposit amount"}
            onChange={(e) => {
              const n = parseFloat(e.target.value || "0");
              onChange({ kind: value.kind, value: Math.max(0, Math.round(value.kind === "percent" ? Math.min(n, 100) * 100 : n * 100)) });
            }}
          />
          {value.kind === "percent" ? <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-steel">%</span> : null}
        </div>
      ) : null}
    </div>
  );
}

export function SalesSettingsPanel({ tenantId, settings, onClose }: { tenantId: string; settings: SalesSettings; onClose: () => void }) {
  const [types, setTypes] = useState<EstimateType[]>(() => settings.estimateTypes.map((t) => ({ ...t })));
  const [invoiceTerms, setInvoiceTerms] = useState(settings.invoiceTerms);
  const [agreementTerms, setAgreementTerms] = useState<Record<string, string>>(() => ({ ...settings.agreementTerms }));
  const [termsDept, setTermsDept] = useState("");
  const dept = useDepartments();
  const [open, setOpen] = useState<string | null>(settings.estimateTypes.length ? null : "__new");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const setType = (id: string, patch: Partial<EstimateType>) => setTypes((ts) => ts.map((t) => (t.id === id ? { ...t, ...patch } : t)));

  async function save() {
    if (types.some((t) => !t.name.trim())) return setError("Give every type a name.");
    setBusy(true);
    setError("");
    try {
      await call("setSalesSettings", { tenantId, estimateTypes: types, invoiceTerms, agreementTerms });
      onClose();
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-4">
      <div>
        <CardTitle className="mb-0">Estimate types and terms</CardTitle>
        <p className="text-xs text-steel">
          A new estimate starts from a type: its terms print on the estimate and show on the customer's signing page, with the
          deposit it asks for and how long the price holds.
        </p>
      </div>

      <div className="space-y-2">
        {types.map((t) => {
          const isOpen = open === t.id;
          return (
            <div key={t.id} className="rounded-lg border border-steel/20">
              <div className="flex items-center gap-2 bg-cream/60 px-2 py-1.5">
                <button type="button" className="p-0.5 text-steel hover:text-ink" onClick={() => setOpen(isOpen ? null : t.id)} aria-label={isOpen ? "Fold" : "Open"}>
                  {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                <Input className="h-7 flex-1 py-0 text-sm font-semibold" value={t.name} placeholder="Type name (Commercial, Service…)" maxLength={80} onChange={(e) => setType(t.id, { name: e.target.value })} />
                <span className="hidden shrink-0 text-[11px] text-steel sm:inline">
                  {t.deposit ? (t.deposit.kind === "percent" ? `${t.deposit.value / 100}% deposit` : `$${(t.deposit.value / 100).toFixed(2)} deposit`) : "no deposit"}
                  {t.validDays ? ` · ${t.validDays} days` : ""}
                  {t.terms.trim() ? " · terms" : ""}
                </span>
                <button type="button" className="p-1 text-steel hover:text-danger" onClick={() => setTypes((ts) => ts.filter((x) => x.id !== t.id))} aria-label={`Remove ${t.name}`}>
                  <Trash2 size={13} />
                </button>
              </div>
              {isOpen ? (
                <div className="space-y-2 p-3">
                  <div className="flex flex-wrap items-end gap-4">
                    <div>
                      <Label>Deposit when signed</Label>
                      <DepositInput value={t.deposit} onChange={(d) => setType(t.id, { deposit: d })} />
                    </div>
                    <div>
                      <Label>Price good for (days)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="365"
                        className="w-28"
                        placeholder="No limit"
                        value={t.validDays ?? ""}
                        onChange={(e) => setType(t.id, { validDays: e.target.value ? Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 1)) : null })}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                      <Label className="mb-0">Terms (printed on the estimate; the customer agrees to them when signing)</Label>
                      <TermsLoader category="Estimate Terms" onLoad={(terms) => setType(t.id, { terms })} />
                    </div>
                    <textarea className={textareaClass} rows={8} maxLength={20000} value={t.terms} placeholder="Payment terms, warranty, what's not included, cancellation…" onChange={(e) => setType(t.id, { terms: e.target.value })} />
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
        {types.length < 20 ? (
          <button
            type="button"
            className="flex items-center gap-1 text-xs font-semibold text-brandorange-dark hover:underline"
            onClick={() => {
              const id = `t${Date.now().toString(36)}`;
              setTypes((ts) => [...ts, { id, name: "", terms: "", validDays: 30, deposit: null }]);
              setOpen(id);
            }}
          >
            <Plus size={13} /> Add a type
          </button>
        ) : null}
      </div>

      <div>
        <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
          <Label className="mb-0">Invoice terms (printed on every invoice)</Label>
          <TermsLoader category="Invoice Terms" onLoad={setInvoiceTerms} />
        </div>
        <textarea className={textareaClass} rows={4} maxLength={20000} value={invoiceTerms} placeholder="Payment due within 30 days. A late fee of 1.5% per month applies after that." onChange={(e) => setInvoiceTerms(e.target.value)} />
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2">
          <Label className="mb-0">Maintenance agreement terms</Label>
          <TermsLoader category="Service Agreements" onLoad={(text) => setAgreementTerms((t) => ({ ...t, [termsDept]: text }))} />
          {dept.departments.length ? (
            <Select className="w-56 py-1 text-xs" value={termsDept} onChange={(e) => setTermsDept(e.target.value)} aria-label="Terms for which department">
              <option value="">Any department (default)</option>
              {dept.departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {agreementTerms[d.id]?.trim() ? " ✓" : ""}
                </option>
              ))}
            </Select>
          ) : null}
        </div>
        <textarea
          className={textareaClass}
          rows={4}
          maxLength={20000}
          value={agreementTerms[termsDept] ?? ""}
          placeholder={termsDept ? "This department's agreement terms (blank = the default)" : "What a maintenance agreement covers, what it doesn't, how to cancel…"}
          onChange={(e) => setAgreementTerms((t) => ({ ...t, [termsDept]: e.target.value }))}
        />
      </div>

      <p className="text-xs text-steel">Commissions (what they're figured on, when they're earned, default rates by employee type) live under Team → Commissions.</p>

      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex gap-2">
        <Button busy={busy} onClick={() => void save()}>
          Save
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}
